'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { ChildAvailabilityItem, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.ts';
import { useChildSponsorship } from '../../../hooks/useChildSponsorship.ts';
import { ChildBioDetailsHeading } from '../../../molecules/child-bio-details/child-bio-details-heading.tsx';
import { ChildBioInfo } from '../../../molecules/child-bio-details/child-bio-info.tsx';
import { SubmitButton } from '../../../molecules/child-bio-details/child-bio-sponsorship-submit-button.tsx';
import { ChildBioSummary } from '../../../molecules/child-bio-details/child-bio-summary.tsx';
import { ChildBioImage } from '../../../molecules/child-bio-image/child-bio-image.tsx';
import { key } from '../../../organisms-cms/child-bio/utils/bioKeys.tsx';
import { getNote } from '../../../organisms-cms/child-bio/utils/childNote.tsx';
type HelpMeChooseResult = {
  childDetails: ChildDetails;
  childAvailability: ChildAvailabilityItem;
};
export const HelpMeChooseResult: React.FC<HelpMeChooseResult> = t0 => {
  const $ = _c(45);
  const {
    childDetails,
    childAvailability
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["bio", "geo", "help-me"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    country,
    correspondenceLanguage,
    globalId,
    localId,
    name,
    hangul
  } = childDetails;
  let t2;
  if ($[1] !== childDetails) {
    t2 = getNote(childDetails);
    $[1] = childDetails;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const note = t2;
  let t3;
  if ($[3] !== country || $[4] !== localId) {
    t3 = countryToIso(country, localId);
    $[3] = country;
    $[4] = localId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const countryIso = t3;
  const countryKey = `geo:countries.${countryIso}.nameS`;
  let t4;
  if ($[6] !== countryKey || $[7] !== t) {
    t4 = t(countryKey);
    $[6] = countryKey;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const translatedCountry = t4;
  let t5;
  if ($[9] !== correspondenceLanguage || $[10] !== t) {
    const languageKey = key("bio:language", correspondenceLanguage);
    t5 = t(languageKey);
    $[9] = correspondenceLanguage;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const translatedLanguage = t5;
  const {
    isSubmitting,
    bioErrors,
    handleSponsorshipSubmit
  } = useChildSponsorship(globalId);
  let t6;
  if ($[12] !== hangul || $[13] !== name) {
    t6 = <div className="sm:hidden"><ChildBioDetailsHeading seoLevel={2} hangul={hangul} name={name} /></div>;
    $[12] = hangul;
    $[13] = name;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== childAvailability || $[16] !== childDetails) {
    t7 = <ChildBioImage details={childDetails} availability={childAvailability} />;
    $[15] = childAvailability;
    $[16] = childDetails;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <div className="ChildBio__media flex-1 align-start">{t6}{t7}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== hangul || $[22] !== name) {
    t9 = <div className="hidden sm:block"><ChildBioDetailsHeading seoLevel={2} hangul={hangul} name={name} /></div>;
    $[21] = hangul;
    $[22] = name;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== childDetails || $[25] !== translatedCountry || $[26] !== translatedLanguage) {
    t10 = <ChildBioSummary className="mb-0 sm:pb-comfy-sm text-sm order-1" details={childDetails} translatedCountry={translatedCountry} translatedLanguage={translatedLanguage} />;
    $[24] = childDetails;
    $[25] = translatedCountry;
    $[26] = translatedLanguage;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== bioErrors || $[29] !== childDetails || $[30] !== handleSponsorshipSubmit || $[31] !== isSubmitting) {
    t11 = <SubmitButton className="order-2 sm:order-last" bioErrors={bioErrors} details={childDetails} isSubmitting={isSubmitting} handleSponsorshipSubmit={handleSponsorshipSubmit} />;
    $[28] = bioErrors;
    $[29] = childDetails;
    $[30] = handleSponsorshipSubmit;
    $[31] = isSubmitting;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== childDetails || $[34] !== countryKey || $[35] !== note) {
    t12 = <div className="order-3 sm:pb-comfy-xl"><ChildBioInfo details={childDetails} countryKey={countryKey} note={note} /></div>;
    $[33] = childDetails;
    $[34] = countryKey;
    $[35] = note;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t9) {
    t13 = <div className="ChildBio__summary flex flex-col flex-1 gap-comfy-sm sm:gap-0 sm:pt-0">{t9}{t10}{t11}{t12}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t9;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t13 || $[43] !== t8) {
    t14 = <div className="HelpMeChooseResult"><div className="ChildBio ChildBio__flex flex flex-col"><div className="ChildBio__content flex flex-col sm:flex-row gap-comfy-sm sm:gap-comfy-xl">{t8}{t13}</div></div></div>;
    $[42] = t13;
    $[43] = t8;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  return t14;
};